/**
 * The not-yet-saved edits this person made to each board, field by field
 * (boardChangeSet.js explains why a board no longer saves whole).
 *
 * Every local edit records itself here — updateBoard, undo/redo and the
 * board-settings edit each call `noteBoardEdit(prev, next)` — and the save
 * takes the accumulated set (`takeBoardChanges`), writes it, and puts it back
 * if the write failed (`restoreBoardChanges`). Keyed by board id at module
 * level, so two tabs on one board share one log, like they share one board.
 * Teammates' changes never pass through here: they arrive as records and are
 * never re-sent by this person.
 */
import { diffBoards } from './boardsStore';
import { mergeChanges } from './boardChangeSet';

const pending = new Map(); // boardId -> Map<recordId, change>

/** Record what changed between two versions of one board. Returns the board id. */
export function noteBoardEdit(prev, next) {
  if (!next?.id) return null;
  const changes = diffBoards(prev, next);
  if (!changes.size) return next.id;
  if (!pending.has(next.id)) pending.set(next.id, new Map());
  mergeChanges(pending.get(next.id), changes);
  return next.id;
}

/** True when the board has edits this log knows about. */
export const hasBoardChanges = (boardId) => (pending.get(boardId)?.size || 0) > 0;

/** Take (and clear) the board's pending changes for a save. */
export function takeBoardChanges(boardId) {
  const changes = pending.get(boardId) || new Map();
  pending.delete(boardId);
  return changes;
}

/** A save failed: put its changes back, under anything noted since. */
export function restoreBoardChanges(boardId, changes) {
  if (!changes?.size) return;
  const since = pending.get(boardId);
  const merged = mergeChanges(new Map(changes), since || new Map());
  pending.set(boardId, merged);
}

/** Forget a board (deleted, or its edits superseded by a whole write). */
export function clearBoardChanges(boardId) {
  pending.delete(boardId);
}

/** Tests only. */
export function resetBoardChangeLogForTests() {
  pending.clear();
}
